<template>
  <div class="onboarding auth-panel">
    <!-- Left panel -->
    <div class="auth-panel-left">
      <div class="auth-panel-left-inner" v-if="ready">
        <!-- Progress indicator, and the way out of an optional slide (Skip, I'll do this later). The
             way out sits up here, small, so a click aimed at the primary button can't land on it. -->
        <div class="slide-top">
          <div class="auth-progress">
            <span class="auth-progress-pip" />
            <span v-for="(_, i) in slides" :key="i" :class="['auth-progress-pip', { active: i == currentIndex }]" />
          </div>
          <transition name="fade" mode="out-in">
            <button v-if="currentSlide.skip" :key="currentSlide.skip.text" type="button" class="skip-link"
              :data-e2e="currentSlide.skip.e2e" :disabled="loading" @click="runAction(currentSlide.skip.action)">
              {{ currentSlide.skip.text }}
            </button>
          </transition>
        </div>

        <transition name="fade" mode="out-in">
          <div :key="currentIndex" class="slide-content">
            <h2 class="title">{{ currentSlide.title }}</h2>
            <p class="desc">{{ currentDesc }}</p>

            <!-- Card-style options (Basic / Expert) -->
            <div class="card-options" v-if="currentSlide.cardOptions">
              <button v-for="opt in currentSlide.cardOptions" :data-e2e="opt.value" :key="opt.value"
                :class="['card-option', { selected: selectedCardOption === opt.value }]" @click="onSelect(opt)">
                <span class="card-option__icon">
                  <icon-basic v-if="opt.value === 'basic'" />
                  <icon-expert v-else-if="opt.value === 'expert'" />
                </span>
                <span class="card-option__label">{{ opt.label }}</span>
                <span v-if="opt.description" class="card-option__desc">{{ opt.description }}</span>
              </button>
            </div>

            <!-- Pill-style options (workspace type) -->
            <div class="pill-options" v-else-if="currentSlide.options">
              <OptionButton v-for="opt in currentSlide.options" :key="opt.value" :label="opt.label"
                @click="onSelect(opt)" />
            </div>

            <!-- Questionnaire (guided onboarding P1, docs/frontend-guided-onboarding-proposal.md).
                 One tap per question, nothing required; an "Other…" chip reveals a short field. -->
            <div v-if="currentSlide.questions" class="questions">
              <div v-for="q in currentSlide.questions" :key="q.key" class="question">
                <div class="question__label">{{ q.label }}</div>
                <div class="chips">
                  <button v-for="opt in q.options" :key="opt.value" type="button"
                    :data-e2e="`chip-${q.key}-${opt.value}`"
                    :class="['chip', { selected: answers[q.key] === opt.value }]"
                    @click="selectAnswer(q, opt.value)">
                    {{ opt.label }}
                    <span v-if="opt.hint" class="chip__hint">{{ opt.hint }}</span>
                  </button>
                </div>
                <input v-if="q.otherKey && answers[q.key] === 'other'" class="question__other"
                  v-model="answers[q.otherKey]" :placeholder="q.otherPlaceholder" maxlength="120"
                  :data-e2e="`other-${q.key}`" />
              </div>
            </div>

            <!-- First workflow (guided onboarding P2). The server ranks the starters against the
                 answers just given; the first is picked for you, so the common path is one click. -->
            <div v-if="currentSlide.starters && !describing" class="starters">
              <div v-if="startersError" class="starters__error">{{ startersError }}</div>
              <div v-else class="starters__grid">
                <button v-for="starter in visibleStarters" :key="starter.key" type="button"
                  :data-e2e="`starter-${starter.key}`"
                  :class="['starter', { selected: selectedStarterKey === starter.key }]"
                  @click="selectedStarterKey = starter.key">
                  <span class="starter__emoji">{{ starter.emoji }}</span>
                  <span class="starter__name">{{ starter.name }}</span>
                  <span class="starter__desc">{{ starter.description }}</span>
                </button>
              </div>
              <p v-if="moreStarters || typed.available" class="starters__links">
                <button v-if="moreStarters" type="button"
                  class="starters__link" data-e2e="more-starters" @click="showAllStarters = true">
                  More starters</button><!--
                P3 (D-6): a need no starter covers goes to GNey. Offered only where it can run.
                --><template v-if="typed.available">{{ moreStarters ? ' · or ' : 'Or ' }}<button type="button"
                  class="starters__link" data-e2e="describe-own" @click="openDescribe">describe your own…</button>
                  and GNey builds it</template>
              </p>
              <p v-if="!typed.available && typed.reason" class="starters__unavailable" data-e2e="describe-own-unavailable">
                {{ typedUnavailableLine }}
              </p>
            </div>

            <!-- Describe your own (P3, D-6): the need in a sentence, and the three chips that decide
                 its shape. Nothing is pre-selected; an unanswered chip is left to GNey. -->
            <div v-if="currentSlide.starters && describing" class="describe" data-e2e="describe-own-form">
              <textarea v-model="need" class="describe__need" rows="3" :maxlength="needMaxLength"
                placeholder="e.g. Visitor sign-ins at reception: badge number, and the host approves before check-in"
                data-e2e="describe-need" />
              <div class="describe__count">{{ need.length }}/{{ needMaxLength }}</div>
              <div v-for="q in designQuestions" :key="q.key" class="question">
                <div class="question__label">{{ q.label }}</div>
                <div class="chips">
                  <button v-for="opt in q.options" :key="opt.value" type="button"
                    :data-e2e="`chip-${q.key}-${opt.value}`"
                    :class="['chip', { selected: isDesignChip(q, opt.value) }]"
                    @click="toggleDesignChip(q, opt.value)">
                    {{ opt.label }}
                  </button>
                </div>
              </div>
              <button type="button" class="starters__link" data-e2e="back-to-starters" @click="describing = false">
                Back to starters
              </button>
            </div>

            <!-- A typed build in flight (P3): the server records each phase and the wizard polls. -->
            <div v-if="currentSlide.ready && building" class="build-progress" data-e2e="build-progress">
              <div v-for="step in buildSteps" :key="step.key"
                :class="['build-progress__row', `build-progress__row--${step.status}`]"
                :data-e2e="`build-step-${step.key}-${step.status}`">
                <span class="build-progress__mark" />
                {{ step.label }}
              </div>
            </div>

            <!-- Ready: what was built, and a way straight into it. -->
            <div v-if="currentSlide.ready && built" class="ready">
              <div class="ready__counts">
                <div><b>{{ builtPreview.fields.length }}</b>fields</div>
                <div><b>{{ builtPreview.states.length }}</b>states</div>
                <div><b>{{ builtPreview.activities.length }}</b>activities</div>
                <div><b>{{ built.samples || 0 }}</b>sample records</div>
              </div>
              <p v-if="built.samples" class="ready__note">
                The samples are marked in the list, and you can clear them whenever you have real records.
              </p>
            </div>

            <!-- Email verification step-up (docs/identity-email-verification-proposal.md, D-6):
                 an account that predates verification proves its address once, then creates. -->
            <fts-verification-code v-if="currentSlide.verify && stepUp" :verification="stepUp"
              :busy="stepUpBusy || loading" :verified="stepUpDone" :error="stepUpError"
              button-text="Verify and create workspace" @submit="confirmStepUp" @resend="resendStepUp" />

            <!-- Workspace name input -->
            <div v-if="currentSlide.input" class="input-group">
              <input v-model="form[currentInputName]" :placeholder="currentInputPlaceholder"
                @blur="$v.form[currentInputName] && $v.form[currentInputName].$touch()" class="input-box" />
              <div v-if="$v.form[currentInputName] && $v.form[currentInputName].$error" class="fts-form-error">
                <span v-if="!$v.form[currentInputName].required">Workspace name is required.</span>
                <span v-else-if="!$v.form[currentInputName].format">Invalid characters in workspace name.</span>
              </div>
            </div>
          </div>
        </transition>

        <div class="slide-actions">
          <transition name="fade" mode="out-in">
            <button v-if="currentSlide.button" :key="currentSlide.button.text" :data-e2e="currentSlide.button.e2e"
              class="auth-primary-btn" :disabled="primaryDisabled"
              @click="handleButton">
              {{ currentSlide.button.text }}
            </button>
          </transition>
          <span v-if="currentSlide.buttonHint" class="slide-actions__hint">{{ currentSlide.buttonHint }}</span>
          <button v-if="currentSlide.secondary" class="secondary-btn" :data-e2e="currentSlide.secondary.e2e"
            :disabled="loading" @click="runAction(currentSlide.secondary.action)">
            {{ currentSlide.secondary.text }}
          </button>
        </div>
        <p v-if="currentSlide.studioLink" class="studio-link">
          {{ currentSlide.studioLink.lead }}
          <a href="#" data-e2e="start-in-studio" @click.prevent="startInStudio">{{ currentSlide.studioLink.text }}</a>
        </p>
      </div>

      <div>
        <button class="auth-back-btn" @click="back" v-if="canGoBack">
          <font-awesome-icon class="back-icon" :icon="['fas', 'angle-left']" />
          Back
        </button>
      </div>
    </div>

    <!-- spinner -->
    <fts-spinner full-screen tabindex="0" v-if="loading" />

    <!-- Right panel -->
    <div class="auth-panel-right">
      <transition name="slide-right" mode="out-in">
        <!-- On the first-workflow slide the panel previews the selected starter, so the choice is
             made on what it contains rather than on its name. -->
        <!-- While describing, the panel shows what GNey will build from, filling in as it is typed. -->
        <div v-if="currentSlide.starters && describing" key="design-brief" class="answer-summary"
          data-e2e="design-brief">
          <div class="answer-summary__head">What GNey will build from</div>
          <div v-for="row in briefSummary" :key="row.key"
            :class="['answer-summary__row', 'answer-summary__row--wrap', { 'answer-summary__row--empty': !row.value }]"
            :data-e2e="`design-brief-${row.key}`">
            <span class="answer-summary__label">{{ row.label }}</span>
            <span class="answer-summary__value">{{ row.value || '—' }}</span>
          </div>
        </div>
        <div v-else-if="currentSlide.starters && selectedStarter" :key="`preview-${selectedStarter.key}`"
          class="starter-preview" data-e2e="starter-preview">
          <div class="starter-preview__head">
            <span class="starter-preview__emoji">{{ selectedStarter.emoji }}</span>
            <div>
              <div class="starter-preview__name">{{ selectedStarter.name }}</div>
              <div class="starter-preview__sub">What the starter includes. Change anything later.</div>
            </div>
          </div>
          <div class="starter-preview__label">Workflow</div>
          <div class="starter-preview__states">
            <span v-for="(state, i) in selectedStarter.states" :key="state" class="starter-preview__step">
              <span :class="['starter-preview__state', `starter-preview__state--${stateTone(state)}`]"
                :data-e2e="`starter-state-${state}`">{{ state }}</span><span
                v-if="i < selectedStarter.states.length - 1" class="starter-preview__arrow">{{ stateJoin(i) }}</span>
            </span>
          </div>
          <div class="starter-preview__label">Fields</div>
          <div class="starter-preview__chips">
            <span v-for="field in selectedStarter.fields" :key="field.name" class="starter-preview__chip">
              {{ field.name }}
            </span>
          </div>
          <div class="starter-preview__label">Steps</div>
          <div class="starter-preview__chips">
            <span v-for="activity in selectedStarter.activities" :key="activity"
              class="starter-preview__chip starter-preview__chip--step">{{ activity }}</span>
          </div>
          <div class="starter-preview__foot">
            {{ selectedStarter.samples }} sample records · installs in seconds
          </div>
        </div>
        <!-- On the question slides the panel keeps a running summary of what has been answered, as in
             the proposal mockup. One stable key across both slides, so it fills in rather than
             sliding out and back in when the second slide opens. -->
        <div v-else-if="currentSlide.questions" key="answer-summary" class="answer-summary"
          data-e2e="answer-summary">
          <div v-for="row in answerSummary" :key="row.key"
            :class="['answer-summary__row', { 'answer-summary__row--empty': !row.value }]"
            :data-e2e="`answer-summary-${row.key}`">
            <span class="answer-summary__label">{{ row.label }}</span>
            <span class="answer-summary__value">{{ row.value || '—' }}</span>
          </div>
        </div>
        <img v-else-if="currentSlide.image" :key="currentIndex" :src="currentSlide.image" class="auth-illustration" />
      </transition>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);
import { required } from 'vuelidate/lib/validators';
import FTSSpinner from '../../elements/FTSSpinner';
import FTSVerificationCode from '../../administration/FTSVerificationCode';
import OptionButton from '../FTSOptionsButtons.vue';
import IconBasic from '../../icons/IconBasic.vue';
import IconExpert from '../../icons/IconExpert.vue';
import OnboardingSvg1 from '../../../assets/images/onboarding/Illustration-1.svg';
import OnboardingSvg2 from '../../../assets/images/onboarding/Illustration-2.svg';
import OnboardingSvg3 from '../../../assets/images/onboarding/Illustration-3.svg';
import { authorizationStore } from '../../store/authorizationStore';
import { clientStore } from '../../store/clientStore';
import LocalStorageService from '../../store/LocalStorageService';
import webAppConfig from '../../commons/webApp.config';
import designValidator from '../../commons/designValidator';
import commons from '../../commons/commons';
import { signupAttribution, heardFromPrefill } from '../../store/attribution';
import { settingsRole, SettingsRole } from '../../settings/settingsAccess';
import { markOnboardingInProgress, readOnboardingInProgress, clearOnboardingInProgress } from '../../commons/onboardingProgress';

const slideFinal = {
  id: 'final',
  title: 'Name your workspace',
  desc: 'You can change the name or create more later.',
  input: { placeholder: 'My workspace', name: 'workspace_name' },
  button: { text: 'Create workspace', action: 'handleFinalButton', e2e: 'create-workspace-btn' },
  image: OnboardingSvg3,
};

/* Sign-up from the website's AutoCount page (?solution=autocount; docs/integration-autocount-onboarding-ux-proposal.md
   §8.2 V3 and W, in the SVN repo). The company is the only question, and the next stop is Settings › AutoCount,
   whose step 1 makes the pairing code as it opens. */
const SOLUTION_AUTOCOUNT = 'autocount';
const AUTOCOUNT_SETTINGS = { name: 'settings', params: { section: 'autocount' } };
const slideCompany = {
  ...slideFinal,
  title: 'Name your company',
  desc: 'Usually the company in your AutoCount account book. You can rename it later.',
  input: { placeholder: 'ABC Trading Sdn Bhd', name: 'workspace_name' },
  button: { text: 'Continue', action: 'handleFinalButton', e2e: 'create-workspace-btn' },
};

/* Guided onboarding P1 (docs/frontend-guided-onboarding-proposal.md, D-3). Two slides of one-tap
   questions, asked after the workspace exists so closing the tab never costs the user anything.
   The values are the server's vocabulary (Services/OnboardingAnswers.cs) — a value that is not on
   this list is dropped there rather than stored, so the two must stay in step. */
const QUESTION_YOU = [
  {
    key: 'heardFrom',
    otherKey: 'heardFromOther',
    otherPlaceholder: 'Where did you hear about us?',
    label: 'Where did you hear about Inistate?',
    options: [
      { value: 'google', label: 'Google search' },
      { value: 'linkedin', label: 'LinkedIn' },
      { value: 'facebook', label: 'Facebook or Instagram' },
      { value: 'x', label: 'X' },
      { value: 'reddit', label: 'Reddit' },
      { value: 'youtube', label: 'YouTube' },
      { value: 'newsletter', label: 'A newsletter' },
      { value: 'friend', label: 'A friend or colleague' },
      { value: 'marketplace', label: 'n8n / Zapier / Make' },
      { value: 'ai_directory', label: 'Claude / ChatGPT directory' },
      { value: 'event', label: 'Event or webinar' },
      { value: 'other', label: 'Other…' },
    ],
  },
  {
    key: 'role',
    otherKey: 'roleOther',
    otherPlaceholder: 'What do you do?',
    label: 'What best describes your role?',
    options: [
      { value: 'founder', label: 'Founder / Owner' },
      { value: 'operations', label: 'Operations' },
      { value: 'hr', label: 'HR & Admin' },
      { value: 'finance', label: 'Finance' },
      { value: 'sales', label: 'Sales & Marketing' },
      { value: 'it', label: 'IT & Engineering' },
      { value: 'consultant', label: 'Consultant / Partner' },
      { value: 'personal', label: 'Student / Personal' },
      { value: 'other', label: 'Other…' },
    ],
  },
];

const QUESTION_PURPOSE = [
  {
    key: 'purpose',
    label: 'What is this workspace for?',
    options: [
      { value: 'team', label: 'My team or company', summary: 'My team' },
      { value: 'client', label: 'Client projects' },
      { value: 'personal', label: 'Personal' },
      { value: 'exploring', label: 'Just exploring' },
    ],
  },
  {
    key: 'teamSize',
    label: 'How many people will use it?',
    options: [
      { value: 'just_me', label: 'Just me' },
      { value: '2-10', label: '2–10' },
      { value: '11-50', label: '11–50' },
      { value: '51-200', label: '51–200' },
      { value: '200+', label: '200+' },
    ],
  },
  {
    key: 'industry',
    label: 'Which industry?',
    options: [
      { value: 'hr', label: 'HR & people' },
      { value: 'finance', label: 'Finance & accounting' },
      { value: 'procurement', label: 'Procurement' },
      { value: 'it_service', label: 'IT & service desk' },
      { value: 'healthcare', label: 'Healthcare' },
      { value: 'legal', label: 'Legal & compliance' },
      { value: 'manufacturing', label: 'Manufacturing & logistics' },
      { value: 'education', label: 'Education' },
      { value: 'professional_services', label: 'Professional services' },
      { value: 'other', label: 'Other' },
    ],
  },
];

/* The "heard from" chip a source suggests now lives in store/attribution.js, because the sign-up
   sends the same value: the server stores what was offered, so a kept prefill is not read as
   independent evidence (docs/identity-signup-funnel-proposal.md §4.2.4). */

/* The four colours the starters give their states (OnboardingStartersTests keeps them to these), as
   the tone the preview strip paints: where a record starts, on its way, done, stopped. */
const STATE_TONES = {
  '#616161': 'start',
  '#ffab00': 'progress',
  '#00c853': 'done',
  '#d50000': 'stopped',
};

/* Describe your own (P3, D-3 C2-C4). The values are the server's vocabulary
   (OnboardingDescription), which drops anything it did not publish, so the two lists move together. */
const DESIGN_QUESTIONS = [
  {
    key: 'submitter',
    label: 'Who submits?',
    options: [
      { value: 'team', label: 'Anyone in the team' },
      { value: 'me', label: 'Only me' },
      { value: 'outside', label: 'People outside the team' },
    ],
  },
  {
    key: 'approval',
    label: 'Does it need approval?',
    options: [
      { value: 'none', label: 'No' },
      { value: 'one', label: 'One approver' },
      { value: 'two', label: 'Two levels' },
    ],
  },
  {
    key: 'track',
    label: 'Also track',
    multi: true,
    options: [
      { value: 'amount', label: 'Amount' },
      { value: 'due_date', label: 'Due date' },
      { value: 'attachment', label: 'Attachment' },
      { value: 'priority', label: 'Priority' },
      { value: 'counterparty', label: 'Customer / vendor' },
    ],
  },
];

const NEED_MAX_LENGTH = 300;

/** How often the wizard asks how a typed build is going; a design takes 15-40 s. */
const BUILD_POLL_MS = 2000;

const BUILD_FAILURES = {
  invalid_design: "GNey's draft didn't validate.",
  create_failed: "The module couldn't be created.",
  design_error: "GNey couldn't be reached just now.",
};

const TYPED_UNAVAILABLE = {
  credits: "Describing your own needs AI credits, and this workspace has none left. Pick a starter above, or start in Studio.",
  not_admin: 'Only a workspace admin can have GNey build a workflow. Pick a starter above, or start in Studio.',
};

export default {
  components: { 'fts-spinner': FTSSpinner, 'fts-verification-code': FTSVerificationCode, OptionButton, IconBasic, IconExpert },
  async created() {
    if (this.$route.name === 'setup-workspace') {
      try {
        await clientStore.dispatch("loadWorkspace", { setupMode: true });
        if (clientStore.state.workspace && clientStore.state.workspace.id) {
          // A workspace already exists. Normally that means the wizard is done and this is a
          // stray visit — except when this tab created it moments ago and the questionnaire is
          // still open, in which case a refresh should resume it rather than eject the user. The
          // marker is per-tab and per-workspace, so an established user opening /setup-workspace
          // is still sent home (guided onboarding P1, D-8).
          if (await this.resumeQuestionnaire(clientStore.state.workspace.id)) return;

          // "Sign in to connect AutoCount": an admin of the open workspace goes to its AutoCount page.
          // Anyone else goes home as before, since Settings › AutoCount is for workspace admins.
          if (this.autocountIntent && settingsRole() === SettingsRole.ADMIN) {
            this.$router.replace(AUTOCOUNT_SETTINGS);
            return;
          }

          this.$router.replace({ name: this.isLiteMode ? 'lite' : 'dashboard' });
        }
      } catch (err) {
        // No workspace found — continue with onboarding
        if (!(err.status === 404)) {
          this.$toasted.global.error(err);
        }
      }
    }
  },
  async mounted() {
    try {
      this.loading = true;
      // The runtime app config carries clientFirstOnboarding, and `slides` reads it. Every shell
      // loads it on mount (Client, Lite, Studio, FTSLogin); this view has to as well, or a
      // refresh straight onto /setup-workspace — which never passes through FTSLogin — would
      // render the retired Basic/Expert fork and could still mint a lite workspace. It resolves
      // before `ready`, so the wizard never renders against an unknown flag.
      await webAppConfig.getConfig();
      await authorizationStore.dispatch("profile");
      this.ready = true;
    } catch(err) {
      this.$toasted.global.error(err);
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    this.stopBuildPoll();
  },
  metaInfo() {
    return { title: 'Inistate | ' + this.currentSlide.title };
  },

  data() {
    return {
      isWorkspacesAccess: false,
      ready: false,
      currentIndex: 0,
      form: {},
      selectedCardOption: "basic",
      loading: false,
      // Guided onboarding P1. Every key is declared up front so the "Other…" fields stay reactive.
      answers: {
        heardFrom: null, heardFromOther: '',
        role: null, roleOther: '',
        purpose: null, teamSize: null, industry: null,
      },
      workspaceCreated: false,
      // Guided onboarding P2: the starter catalog as the server ranked it, and what was built.
      starters: [],
      startersError: null,
      selectedStarterKey: null,
      showAllStarters: false,
      built: null,
      // P3: describe your own. `typed` is the server's word on whether GNey can build here.
      typed: { available: false, reason: null },
      describing: false,
      need: '',
      design: { submitter: null, approval: null, track: [] },
      building: null,
      buildFailure: null,
      buildPoll: null,
      // email verification step-up (D-6)
      stepUp: null,
      stepUpBusy: false,
      stepUpError: null,
      stepUpDone: false,
    };
  },

  computed: {
    username() {
      return authorizationStore.state.userProfile.displayName;
    },
    isLiteMode() {
      return clientStore.state.isLiteMode;
    },
    // Arrived from the website's AutoCount page: see slideCompany.
    autocountIntent() {
      const query = this.$route && this.$route.query;
      return !!query && String(query.solution || '').toLowerCase() === SOLUTION_AUTOCOUNT;
    },
    // Ruled 2026-08-29: no Basic/Expert fork. Naming the workspace is the whole wizard and the
    // Client shell is home for every new workspace (SS06115). The AutoCount path takes it whatever
    // the flag says, so it never mints a lite workspace.
    clientFirst() {
      return this.autocountIntent || authorizationStore.getters.appClientFirstOnboarding;
    },
    // The questionnaire rides on client-first: it is asked after the workspace exists, which only
    // the single-slide wizard guarantees (guided onboarding P1). The AutoCount path skips it.
    guided() {
      return this.clientFirst && !this.autocountIntent && authorizationStore.getters.appGuidedOnboarding;
    },
    // The first-workflow step (P2) rides on the questionnaire, whose answers rank the starters.
    firstWorkflow() {
      return this.guided && authorizationStore.getters.appGuidedFirstWorkflow;
    },
    slides() {
      if (this.clientFirst) {
        const list = [this.autocountIntent ? slideCompany : slideFinal];
        if (this.guided) list.push(this.slideYou, this.slidePurpose);
        if (this.firstWorkflow) list.push(this.slideWorkflow, this.slideReady);
        // The verification step-up is jumped to by index from startStepUp, so it stays last.
        if (this.stepUp) list.push(this.verifySlide);
        return list;
      }
      const slideWelcome = {
        id: 'welcome',
        title: 'Build your workflow app for free.\nNo coding needed.',
        desc: this.username
          ? `Welcome ${this.username}, one more step to have your own customized software and app!`
          : 'To begin, choose how you want to get started.',
        cardOptions: [
          { value: 'basic', label: 'Basic', description: "New to Inistate? Get started with guided setup and learn as you build your workflow. Perfect for first-timers!" },
          { value: 'expert', label: 'Expert', description: 'Start from scratch in Studio.' },
        ],
        button: { text: 'Next', action: 'onNext', e2e: 'next-btn' },
        image: OnboardingSvg1,
      };
      const list = [slideWelcome, slideFinal];
      if (this.stepUp) {
        list.push(this.verifySlide);
      }
      return list;
    },
    verifySlide() {
      return {
        id: 'verify',
        title: 'Verify your email',
        desc: `We sent a 6-digit code to ${this.stepUp ? this.stepUp.email : ''}. Workspaces come with AI credits, so we check the address first.`,
        verify: true,
        image: OnboardingSvg3,
      };
    },
    slideYou() {
      return {
        id: 'you',
        title: 'A little about you',
        desc: 'Two taps. It helps us point you at the right starting place.',
        questions: QUESTION_YOU,
        skip: { text: 'Skip', action: 'skipSlide', e2e: 'onboarding-skip' },
        button: { text: 'Continue', action: 'saveYou', e2e: 'onboarding-continue' },
        image: OnboardingSvg1,
      };
    },
    slidePurpose() {
      return {
        id: 'purpose',
        title: 'About this workspace',
        desc: 'So the first workflow fits your team.',
        questions: QUESTION_PURPOSE,
        skip: { text: 'Skip', action: 'skipSlide', e2e: 'onboarding-skip' },
        button: { text: 'Continue', action: 'savePurpose', e2e: 'onboarding-continue' },
        image: OnboardingSvg2,
      };
    },
    slideWorkflow() {
      if (this.describing) {
        return {
          id: 'workflow',
          title: 'Describe your own',
          desc: 'Say what you need in a sentence. GNey designs the fields and workflow, and adds sample records.',
          starters: true,
          button: { text: 'Build it with GNey', action: 'buildWithGney', e2e: 'build-with-gney' },
          buttonHint: 'About 30 seconds · uses AI credits',
          skip: { text: "I'll do this later", action: 'later', e2e: 'onboarding-later' },
          studioLink: { lead: 'Prefer a blank canvas?', text: 'Start in Studio' },
          image: OnboardingSvg3,
        };
      }
      return {
        id: 'workflow',
        title: 'Your first workflow',
        desc: this.typed.available
          ? 'Pick a starter. It installs in seconds, with sample records you can clear later. Or describe your own and GNey builds it.'
          : 'Pick a starter. It installs in seconds, with sample records you can clear later.',
        starters: true,
        button: { text: 'Use this starter', action: 'useStarter', e2e: 'use-starter' },
        skip: { text: "I'll do this later", action: 'later', e2e: 'onboarding-later' },
        studioLink: { lead: 'Prefer a blank canvas?', text: 'Start in Studio' },
        image: OnboardingSvg3,
      };
    },
    slideReady() {
      if (this.building) {
        return {
          id: 'ready',
          title: this.building.name ? `Building ${this.building.name}…` : 'Building your workflow…',
          desc: this.building.description ? `From your description: “${this.building.description}”` : 'GNey is designing it now.',
          ready: true,
          button: { text: 'Building…', action: null, e2e: 'building' },
          image: OnboardingSvg3,
        };
      }
      if (this.buildFailure) {
        const why = BUILD_FAILURES[this.buildFailure.reason] || BUILD_FAILURES.design_error;
        return {
          id: 'ready',
          title: "We couldn't build this one",
          desc: `${why} Nothing was created, and your description is kept.`,
          ready: true,
          failed: true,
          button: { text: 'Try again', action: 'buildWithGney', e2e: 'retry-build' },
          secondary: { text: 'Pick a starter', action: 'pickStarter', e2e: 'pick-starter' },
          studioLink: { lead: 'Or', text: 'Start in Studio' },
          image: OnboardingSvg3,
        };
      }
      const name = (this.built && this.built.name) || 'Your workflow';
      const fromGney = this.built && this.built.source === 'gney';
      return {
        id: 'ready',
        title: `${name} is ready`,
        desc: (fromGney ? 'Built by GNey from your description. ' : '') + (this.built && this.built.samples
          ? 'It opens with sample records, so you can see the workflow in use straight away.'
          : 'It is set up and waiting for your first record.'),
        ready: true,
        button: { text: `Open ${name}`, action: 'openFirstWorkflow', e2e: 'open-first-workflow' },
        secondary: { text: 'Open in Studio', action: 'startInStudio', e2e: 'open-in-studio' },
        image: OnboardingSvg3,
      };
    },
    /** "More starters" is offered while some are still hidden. */
    moreStarters() {
      return !this.startersError && this.starters.length > this.visibleStarters.length;
    },
    /** The primary button is off while busy, and until there is something to build. */
    primaryDisabled() {
      if (this.loading) return true;
      const slide = this.currentSlide;
      if (slide.ready && this.building) return true;
      if (slide.starters) return this.describing ? !(this.need || '').trim() : !this.selectedStarter;
      return false;
    },
    needMaxLength() {
      return NEED_MAX_LENGTH;
    },
    designQuestions() {
      return DESIGN_QUESTIONS;
    },
    typedUnavailableLine() {
      return TYPED_UNAVAILABLE[this.typed.reason]
        || "Describing your own needs GNey, which isn't available in this workspace yet. Pick a starter above, or start in Studio.";
    },
    /** The right panel while describing: the brief as GNey will receive it, unanswered rows dimmed. */
    briefSummary() {
      const chips = (key) => {
        const question = DESIGN_QUESTIONS.find(q => q.key === key);
        const picked = [].concat(this.design[key] || []);
        const labels = question.options.filter(o => picked.indexOf(o.value) >= 0).map(o => o.label);
        return labels.length ? labels.join(', ') : null;
      };
      return [
        { key: 'need', label: 'Need', value: (this.need || '').trim() || null },
        { key: 'submitter', label: 'Who submits', value: chips('submitter') },
        { key: 'approval', label: 'Approval', value: chips('approval') },
        { key: 'track', label: 'Also track', value: chips('track') },
        { key: 'industry', label: 'Industry', value: this.answerLabel(QUESTION_PURPOSE, 'industry') },
      ];
    },
    /** The three rows of a typed build, from the phase the server last recorded. */
    buildSteps() {
      const phase = this.building && this.building.phase;
      const seeding = phase === 'seeding';
      const name = this.building && this.building.name;
      return [
        { key: 'design', label: seeding ? 'Designed fields and workflow' : 'Designing fields and workflow', status: seeding ? 'done' : 'active' },
        { key: 'create', label: seeding ? `Created ${name || 'the module'}` : 'Creating the module', status: seeding ? 'done' : 'pending' },
        { key: 'samples', label: 'Adding sample records', status: seeding ? 'active' : 'pending' },
      ];
    },
    visibleStarters() {
      return this.showAllStarters ? this.starters : this.starters.slice(0, 4);
    },
    selectedStarter() {
      return this.starters.find(starter => starter.key === this.selectedStarterKey) || null;
    },
    /**
     * The right-hand panel on the question slides: every answer so far, the unanswered ones dimmed,
     * so the person sees the two slides adding up to one picture of them and their workspace.
     */
    answerSummary() {
      const workspace = clientStore.state.workspace;
      return [
        { key: 'workspace', label: 'Workspace', value: (this.form && this.form.workspace_name) || (workspace && workspace.name) || null },
        { key: 'heardFrom', label: 'Heard from', value: this.answerLabel(QUESTION_YOU, 'heardFrom') },
        { key: 'role', label: 'Role', value: this.answerLabel(QUESTION_YOU, 'role') },
        { key: 'purpose', label: 'Purpose', value: this.answerLabel(QUESTION_PURPOSE, 'purpose') },
        { key: 'teamSize', label: 'Team', value: this.answerLabel(QUESTION_PURPOSE, 'teamSize') },
        { key: 'industry', label: 'Industry', value: this.answerLabel(QUESTION_PURPOSE, 'industry') },
      ];
    },
    builtPreview() {
      const preview = this.built && this.built.preview;
      return {
        fields: (preview && preview.fields) || [],
        states: (preview && preview.states) || [],
        activities: (preview && preview.activities) || [],
      };
    },
    /**
     * Once the workspace exists, Back must never reach the name slide and mint a second one; and once
     * the questions are answered and a starter is on offer, going back would only re-ask them.
     */
    canGoBack() {
      if (this.currentIndex <= 0) return false;
      if (this.workspaceCreated) return this.currentSlide.id === 'purpose';
      return true;
    },
    currentSlide() {
      return this.slides[this.currentIndex] || {};
    },
    currentDesc() {
      return this.currentSlide.desc || '';
    },
    currentInputName() {
      return this.currentSlide.input ? this.currentSlide.input.name : null;
    },
    currentInputPlaceholder() {
      return this.currentSlide.input ? this.currentSlide.input.placeholder : '';
    },
  },

  validations() {
    return {
      form: {
        workspace_name: {
          required,
          format: designValidator.workspaceNameFormat,
        },
      },
    };
  },

  methods: {
    back() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      }
    },
    next() {
      if (this.currentIndex < this.slides.length - 1) this.currentIndex++;
    },
    onNext() {
      this.next();
    },
    async handleFinalButton() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      if (this.loading) return;
      try {
        this.loading = true;
        // Client-first: nothing in the sign-up path mints a lite workspace any more. Existing
        // lite workspaces are untouched (client-home D-4).
        const payload = {
          companyName: this.form.workspace_name,
          type: (!this.clientFirst && this.selectedCardOption === 'basic') ? 'lite' : undefined,
        };

        let res;
        try {
          res = await authorizationStore.dispatch('setupWorkspace', payload);
        } catch (err) {
          // Only the server knows whether the gate is on (Identity.EmailVerification.WorkspaceCreateEnabled),
          // so the step-up starts from its 403 alone. The profile's emailConfirmed is false for every
          // account that predates verification - gate on or off - so it must not pre-empt the request.
          if (err?.response?.status === 403 && err.response.data?.error === 'email_not_verified' && !this.stepUpDone) {
            this.loading = false;
            await this.startStepUp();
            return;
          }
          throw err;
        }
        if (res.data && res.data.workspaceId) {
          // The questions, the starters and the build that follow are all about the new workspace,
          // so every request names it (SS06115).
          clientStore.dispatch('setWorkspaceId', res.data.workspaceId);
        }
        if (this.autocountIntent) {
          // Load the new workspace first: Settings reads its roles to let the admin in.
          await this.refreshWorkspace();
          this.$router.replace(AUTOCOUNT_SETTINGS);
        } else if (this.guided) {
          // The workspace exists from here on. Everything that follows is optional, so a user who
          // closes the tab now keeps the workspace and lands on the dashboard next time.
          this.workspaceCreated = true;
          this.markInProgress(res.data && res.data.workspaceId);
          this.prefillHeardFrom();
          this.goToSlide('you');
        } else if (this.clientFirst) {
          // The first module is added from the Client shell's existing Studio path; the
          // first-workflow slide arrives with P2 (docs/frontend-guided-onboarding-proposal.md).
          this.$router.replace({ name: 'dashboard' });
        } else if (this.selectedCardOption === 'basic') {
          LocalStorageService.setLiteMode(webAppConfig.baseServer, "design");
          this.$router.replace({ name: 'lite-module-hub', query: { start: 'basic' } });
        } else {
          this.$router.replace({ name: 'studio' });
        }
      } catch (err) {
        this.$toasted.global.error(err);
      } finally {
        this.loading = false;
      }
    },
    async startStepUp() {
      try {
        this.stepUpBusy = true;
        this.stepUpError = null;
        const res = await authorizationStore.dispatch('verifyEmailStart');
        this.stepUp = res.verification;
        this.$nextTick(() => { this.currentIndex = this.slides.length - 1; });
      } catch (err) {
        if (err?.response?.data?.error === 'already_verified') {
          this.stepUpDone = true;
          await authorizationStore.dispatch('profile');
          return this.handleFinalButton();
        }
        this.$toasted.error(this.stepUpMessage(err));
      } finally {
        this.stepUpBusy = false;
      }
    },
    async confirmStepUp(code) {
      if (this.stepUpBusy || !this.stepUp) return;
      try {
        this.stepUpBusy = true;
        this.stepUpError = null;
        await authorizationStore.dispatch('verifyEmailConfirm', { id: this.stepUp.id, code });
        this.stepUpDone = true;
        this.stepUpBusy = false;
        // The address is proven: create the workspace without a second click.
        await this.handleFinalButton();
      } catch (err) {
        this.stepUpError = this.stepUpMessage(err);
        if (err?.response?.data?.error === 'session_expired') {
          this.stepUp = null;
        }
      } finally {
        this.stepUpBusy = false;
      }
    },
    async resendStepUp() {
      if (this.stepUpBusy || !this.stepUp) return;
      try {
        this.stepUpBusy = true;
        this.stepUpError = null;
        const res = await authorizationStore.dispatch('verifyEmailResend', { id: this.stepUp.id });
        this.stepUp = res.verification;
        this.$toasted.success(`We sent a new code to ${this.stepUp.email}.`);
      } catch (err) {
        this.stepUpError = this.stepUpMessage(err);
      } finally {
        this.stepUpBusy = false;
      }
    },
    stepUpMessage(err) {
      const data = err?.response?.data;
      if (data && (data.error_description || data.message)) return data.error_description || data.message;
      if (typeof err === 'string') return err;
      return 'Something went wrong. Please try again.';
    },
    handleButton() {
      const action = this.currentSlide.button && this.currentSlide.button.action;
      if (action && this[action] && typeof this[action] === 'function') this[action]();
    },
    onSelect(opt) {
      this.selectedCardOption = opt.value;
      this.form.type = opt.value;
    },

    /* ---------------- guided onboarding (P1) ---------------- */

    /** Tapping the selected chip again clears it — nothing here is required. */
    selectAnswer(question, value) {
      const cleared = this.answers[question.key] === value;
      this.answers[question.key] = cleared ? null : value;
      if (question.otherKey && this.answers[question.key] !== 'other') {
        this.answers[question.otherKey] = '';
      }
    },

    /** What an answer reads as in the summary: the typed text behind "Other…", else the chip's label. */
    answerLabel(questions, key) {
      const value = this.answers[key];
      if (!value) return null;
      const question = questions.find(q => q.key === key);
      if (!question) return null;
      if (value === 'other' && question.otherKey) {
        const typed = (this.answers[question.otherKey] || '').trim();
        if (typed) return typed;
      }
      const option = question.options.find(o => o.value === value);
      if (!option) return null;
      return (option.summary || option.label).replace(/…$/, '');
    },

    /** The strip's tone for a state of the selected starter; neutral when the server sent no colour. */
    stateTone(state) {
      const colors = (this.selectedStarter && this.selectedStarter.stateColors) || {};
      return STATE_TONES[String(colors[state] || '').toLowerCase()] || 'start';
    },

    /** "/" between two outcomes (Approved / Rejected), since a record ends in one of them; "→" otherwise. */
    stateJoin(i) {
      const states = this.selectedStarter.states;
      const outcome = state => ['done', 'stopped'].includes(this.stateTone(state));
      return outcome(states[i]) && outcome(states[i + 1]) ? '/' : '→';
    },

    goToSlide(id) {
      const index = this.slides.findIndex(slide => slide.id === id);
      if (index >= 0) this.currentIndex = index;
    },

    async saveYou() {
      await this.saveAnswers({
        heardFrom: this.answers.heardFrom,
        heardFromOther: this.answers.heardFromOther,
        role: this.answers.role,
        roleOther: this.answers.roleOther,
      });
      this.goToSlide('purpose');
    },

    async savePurpose() {
      await this.saveAnswers({
        purpose: this.answers.purpose,
        teamSize: this.answers.teamSize,
        industry: this.answers.industry,
        completed: true,
      });
      this.finishQuestionnaire();
    },

    /** Skip is a save too: the server records which slide was passed over, not silence. */
    async skipSlide() {
      const slide = this.currentSlide.id;
      await this.saveAnswers({ skipped: [slide], completed: slide === 'purpose' });
      if (slide === 'purpose') this.finishQuestionnaire();
      else this.goToSlide('purpose');
    },

    /**
     * Best-effort by design: these answers steer a recommendation, they do not gate anything, so a
     * failed save must never strand someone in the wizard. The error is logged and the user moves on.
     */
    async saveAnswers(payload) {
      if (this.loading) return;
      try {
        this.loading = true;
        await clientStore.dispatch('saveOnboarding', payload);
      } catch (err) {
        console.error('[Onboarding]', err);
      } finally {
        this.loading = false;
      }
    },

    finishQuestionnaire() {
      if (this.firstWorkflow) {
        this.goToSlide('workflow');
        this.loadStarters();
        return;
      }
      this.clearInProgress();
      this.$router.replace({ name: 'dashboard' });
    },

    runAction(name) {
      if (name && typeof this[name] === 'function') this[name]();
    },

    /* ---------------- first workflow (P2) ---------------- */

    async loadStarters() {
      this.startersError = null;
      try {
        this.loading = true;
        const res = await clientStore.dispatch('getOnboardingStarters');
        this.starters = (res && res.starters) || [];
        this.typed = (res && res.typed) || { available: false, reason: null };
        // The server ranked them for this person; the top one is the recommendation.
        if (!this.selectedStarterKey && this.starters.length) this.selectedStarterKey = this.starters[0].key;
        if (!this.starters.length) this.startersError = 'No starters are available right now. You can start in Studio instead.';
      } catch (err) {
        console.error('[Onboarding]', err);
        this.startersError = "Starters couldn't be loaded. You can start in Studio instead, or try again later.";
      } finally {
        this.loading = false;
      }
    },

    async useStarter() {
      if (!this.selectedStarter || this.loading) return;
      try {
        this.loading = true;
        const res = await clientStore.dispatch('buildFirstWorkflow', { starter: this.selectedStarter.key });
        this.applyBuildState(res);
        // GNey was already building one from a description (another tab, or a refresh): follow that.
        if (this.building) this.startBuildPoll();
        this.goToSlide('ready');
      } catch (err) {
        this.$toasted.global.error(err);
      } finally {
        this.loading = false;
      }
    },

    /* ---------------- describe your own (P3) ---------------- */

    openDescribe() {
      this.describing = true;
    },

    isDesignChip(question, value) {
      return question.multi ? this.design[question.key].indexOf(value) >= 0 : this.design[question.key] === value;
    },

    /** One tap picks, a second tap clears; "Also track" takes several. */
    toggleDesignChip(question, value) {
      if (question.multi) {
        const picked = this.design[question.key];
        const at = picked.indexOf(value);
        this.design[question.key] = at >= 0 ? picked.filter(v => v !== value) : picked.concat(value);
      } else {
        this.design[question.key] = this.design[question.key] === value ? null : value;
      }
    },

    /**
     * Ask GNey to build it. The request can outlast a proxy's patience, so the wizard follows the
     * phases the server records rather than the request alone: a dropped connection keeps polling.
     */
    async buildWithGney() {
      const need = (this.need || '').trim();
      if (!need || this.building) return;

      this.buildFailure = null;
      this.building = { phase: 'designing', description: need };
      this.goToSlide('ready');
      this.startBuildPoll();

      try {
        const res = await clientStore.dispatch('designFirstWorkflow', {
          need,
          submitter: this.design.submitter,
          approval: this.design.approval,
          track: this.design.track,
        });
        this.applyBuildState(res);
      } catch (err) {
        const status = err && err.response && err.response.status;
        if (status === 409) {
          // No longer possible here (credits ran out, or AI was switched off): say so, keep the text.
          this.stopBuildPoll();
          this.building = null;
          this.typed = { available: false, reason: (err.response.data && err.response.data.reason) || null };
          this.describing = false;
          this.goToSlide('workflow');
          return;
        }
        // No response, or a gateway giving up on a long request: the server carries on, and the
        // poll reports the outcome.
        if (!status || status >= 502) return;
        this.stopBuildPoll();
        this.building = null;
        this.buildFailure = { reason: 'design_error', description: need };
      }
    },

    /** One place that reads a build's state, whether from the build request or from polling. */
    applyBuildState(state) {
      if (!state) return;
      const phase = state.phase || (state.build && state.build.phase) || null;

      if (state.moduleId && (!phase || phase === 'ready')) {
        this.stopBuildPoll();
        this.building = null;
        this.buildFailure = null;
        this.built = {
          moduleId: state.moduleId,
          name: state.name,
          emoji: state.emoji,
          samples: state.samples,
          starter: state.starter,
          source: state.source,
          description: state.description,
          preview: state.preview,
        };
        return;
      }

      if (phase === 'failed') {
        this.stopBuildPoll();
        this.building = null;
        this.buildFailure = {
          reason: state.reason || (state.build && state.build.reason) || null,
          description: state.description || null,
        };
        return;
      }

      if (phase === 'designing' || phase === 'seeding') {
        this.building = {
          phase,
          name: state.name || null,
          description: state.description || (this.building && this.building.description) || null,
        };
      }
    },

    startBuildPoll() {
      this.stopBuildPoll();
      this.buildPoll = setInterval(() => this.pollBuild(), BUILD_POLL_MS);
    },

    stopBuildPoll() {
      if (this.buildPoll) clearInterval(this.buildPoll);
      this.buildPoll = null;
    },

    async pollBuild() {
      try {
        const state = await clientStore.dispatch('getOnboarding');
        const workflow = state && state.workspace && state.workspace.firstWorkflow;
        if (this.building && workflow) this.applyBuildState(workflow);
      } catch (err) {
        console.error('[Onboarding]', err);
      }
    },

    /** From a failed build back to the starters; the description stays for another try. */
    pickStarter() {
      this.buildFailure = null;
      this.describing = false;
      this.goToSlide('workflow');
    },

    /** "I'll do this later": recorded, so the dashboard offers it once and not forever. */
    async later() {
      try {
        await clientStore.dispatch('dismissFirstWorkflow');
      } catch (err) {
        console.error('[Onboarding]', err);
      }
      this.clearInProgress();
      this.$router.replace({ name: 'dashboard' });
    },

    startInStudio() {
      this.clearInProgress();
      this.$router.replace({ name: 'studio' });
    },

    async openFirstWorkflow() {
      this.clearInProgress();
      const moduleId = this.built && this.built.moduleId;
      if (!moduleId) {
        this.$router.replace({ name: 'dashboard' });
        return;
      }
      await this.refreshWorkspace();
      // List routes carry the ENCODED module id (FTSListView decodes it); the raw id would open
      // whichever module the hash of that number happens to decode to. t=dv opens the detail view,
      // record beside the list, the way the shell opens a module it has just created.
      this.$router.replace({
        name: 'list-view',
        params: { moduleId: commons.encodeids(Number(moduleId)) },
        query: { t: 'dv' },
      });
    },

    /**
     * Reload the workspace (and so its cache) before leaving the wizard. The workspace was cached
     * before the first workflow existed, and the shell mounts from that cache: with no modules in it,
     * the list view fetches the workspace itself, which aborts the shell's own load, and the shell
     * then offers "Create a module" over the module that was just built. Best effort: the shell
     * still loads the workspace on its own.
     *
     * The shell opens the workspace the cookie names, so that is the one reloaded: the store can
     * still hold a workspace loaded before the wizard was opened from inside the app.
     */
    async refreshWorkspace() {
      const workspace = clientStore.state.workspace;
      const id = this.$cookies.get('wsId') || (workspace && workspace.id);
      if (!id) return;
      try {
        this.loading = true;
        await clientStore.dispatch('loadWorkspace', { id, forceReload: true });
      } catch (err) {
        console.error('[Onboarding]', err);
      } finally {
        this.loading = false;
      }
    },

    /**
     * A refresh mid-questionnaire. Returns true when this tab should stay and carry on, which is
     * only when it is the tab that just created this workspace and the questions are unfinished.
     */
    async resumeQuestionnaire(workspaceId) {
      if (!this.guided || !workspaceId) return false;
      if (this.readInProgress() !== String(workspaceId)) return false;

      let state = null;
      try {
        state = await clientStore.dispatch('getOnboarding');
      } catch (err) {
        console.error('[Onboarding]', err);
        this.clearInProgress();
        return false;
      }

      const workspaceAnswers = (state && state.workspace) || {};
      const workflow = workspaceAnswers.firstWorkflow || {};
      const questionsDone = Boolean(workspaceAnswers.completedAt);
      const workflowOpen = this.firstWorkflow && !workflow.moduleId && !workflow.dismissedAt;

      if (questionsDone && !workflowOpen) {
        this.clearInProgress();
        return false;
      }

      this.workspaceCreated = true;
      this.restoreAnswers(state);
      this.restoreDescription(workflow);
      this.$nextTick(() => {
        const slide = questionsDone ? 'workflow' : this.firstUnansweredSlide(state);
        this.goToSlide(slide);
        if (slide === 'workflow') this.loadStarters();
        const phase = workflow.build && workflow.build.phase;
        if (slide === 'workflow' && (phase === 'designing' || phase === 'seeding')) {
          // A typed build was running when the page reloaded: follow it, not a second one.
          this.applyBuildState(workflow);
          this.goToSlide('ready');
          this.startBuildPoll();
        }
      });
      return true;
    },

    /** A description typed before a refresh or a failed build is still there to build from. */
    restoreDescription(workflow) {
      if (!workflow || workflow.source !== 'gney' || !workflow.description) return;
      this.describing = true;
      this.need = workflow.description;
      this.design = {
        submitter: workflow.submitter || null,
        approval: workflow.approval || null,
        track: [].concat(workflow.track || []),
      };
    },

    firstUnansweredSlide(state) {
      const profile = (state && state.profile) || {};
      const skipped = ((state && state.workspace && state.workspace.skipped) || []);
      const youDone = skipped.indexOf('you') >= 0 || profile.heardFrom || profile.role;
      return youDone ? 'purpose' : 'you';
    },

    restoreAnswers(state) {
      const profile = (state && state.profile) || {};
      const workspace = (state && state.workspace) || {};
      this.answers.heardFrom = profile.heardFrom || null;
      this.answers.heardFromOther = profile.heardFromOther || '';
      this.answers.role = profile.role || null;
      this.answers.roleOther = profile.roleOther || '';
      this.answers.purpose = workspace.purpose || null;
      this.answers.teamSize = workspace.teamSize || null;
      this.answers.industry = workspace.industry || null;
    },

    /** Start the "heard from" question on whatever the marketing site already saw. */
    prefillHeardFrom() {
      if (this.answers.heardFrom) return;
      try {
        // The same value the sign-up sent as heardFromPrefill, from the same source.
        const match = heardFromPrefill(signupAttribution());
        if (match) this.answers.heardFrom = match;
      } catch (err) {
        // A malformed or absent cookie just means no head start.
      }
    },

    markInProgress(workspaceId) {
      markOnboardingInProgress(workspaceId);
    },
    readInProgress() {
      return readOnboardingInProgress();
    },
    clearInProgress() {
      clearOnboardingInProgress();
    },
  },
};
</script>

<style lang="scss">
@use "./src/assets/scss/components/auth-panel";
</style>

<style lang="scss" scoped>
// Onboarding-specific styles (layout shared via auth-panel.scss above)

.slide-content {
  display: flex;
  flex-direction: column;
}

.title {
  font-size: 32px;
  font-weight: var(--inistate-font-weight-bold, 700);
  letter-spacing: -0.02em;
  color: var(--inistate-text-primary-color);
  white-space: pre-line;
  margin-bottom: 16px;
  line-height: var(--inistate-heading-text-32--line-height, 40px);
}

.desc {
  font-size: var(--inistate-label-text-16, 14px);
  color: var(--fts-text);
  margin: 0 0 48px;
  line-height: 1.5;
  opacity: 0.75;
}

.card-options {
  display: flex;
  gap: 24px;
}

.card-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 239px;
  gap: 8px;
  padding: 24px;
  border: 1.5px solid var(--fts-border);
  border-radius: var(--inistate-rounded-4, 4px);
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: color 0.2s, border-color 0.2s, background 0.2s, box-shadow 0.2s;
}

.card-option:hover {
  background: var(--inistate-color-neutral-50, #f6f6f6);
  border-color: var(--inistate-color-neutral-200, #e0e0e0);
}

.card-option.selected {
  background: var(--inistate-color-primary-50, #f1f9fc);
  border-color: var(--fts-primary);
}

.card-option__icon {
  line-height: 1;
  margin-bottom: 12px;
  color: var(--fts-gray-500);
}

.card-option .card-option__label,
.card-option .card-option__icon {
  transition: color 0.2s;
}

.card-option.selected .card-option__icon,
.card-option.selected .card-option__label {
  color: var(--fts-primary);
}

.card-option__label {
  font-size: 24px;
  font-weight: var(--inistate-font-weight-medium, 500);
  line-height: 24px;
  color: var(--inistate-text-primary-color);
}

.card-option__desc {
  font-size: var(--inistate-label-text-16, 14px);
  color: var(--inistate-text-secondary-color);
  line-height: var(--inistate-label-text-16--line-height, 20px);
  font-weight: var(--inistate-font-weight-light, 400);
}

.pill-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* Guided onboarding questionnaire (P1). Chips are fully round per the design system; the selected
   one carries the same 2px primary border the system uses for focus, so selection reads at a glance
   without a fill that fights the page. */
.questions {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.question__label {
  font-size: var(--inistate-label-text-16, 14px);
  font-weight: var(--inistate-font-weight-medium, 500);
  color: var(--inistate-text-primary-color);
  margin-bottom: 10px;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 0 14px;
  border-radius: var(--inistate-rounded-999, 999px);
  border: 1px solid var(--fts-border, #e0e0e0);
  background: #fff;
  color: var(--inistate-text-secondary-color, #595959);
  font-size: var(--inistate-label-text-16, 13px);
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s, background 0.2s;
}

.chip:hover {
  background: var(--inistate-color-neutral-50, #f6f6f6);
}

.chip.selected {
  border: 2px solid var(--fts-primary);
  background: var(--inistate-color-primary-50, #f1f9fc);
  color: var(--fts-primary);
  font-weight: var(--inistate-font-weight-medium, 500);
  padding: 0 13px;
}

.chip__hint {
  font-size: 11px;
  font-weight: var(--inistate-font-weight-light, 400);
  color: var(--inistate-text-secondary-color, #878787);
}

.question__other {
  margin-top: 10px;
  width: 100%;
  max-width: 360px;
  padding: 8px 12px;
  border-radius: var(--inistate-rounded-4, 4px);
  border: 1px solid var(--fts-border, #e0e0e0);
  font-size: var(--inistate-label-text-16, 14px);
  color: var(--fts-text);
  background: #fff;
  outline: none;
}

.question__other:focus {
  border: 2px solid var(--fts-primary);
  padding: 7px 11px;
}

.slide-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* First workflow (P2). Starter cards use the Basic/Expert card vocabulary this wizard already has —
   radius 4, 1.5px border, primary-50 fill with a primary border when selected — so the new step
   looks like it belongs to the same wizard. */
.starters {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.starters__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.starter {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  padding: 12px 14px;
  border: 1.5px solid var(--fts-border, #e0e0e0);
  border-radius: var(--inistate-rounded-4, 4px);
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.starter:hover {
  background: var(--inistate-color-neutral-50, #f6f6f6);
}

.starter.selected {
  border-color: var(--fts-primary);
  background: var(--inistate-color-primary-50, #f1f9fc);
}

.starter__emoji {
  font-size: 20px;
  line-height: 1;
  margin-bottom: 4px;
}

.starter__name {
  font-size: var(--inistate-label-text-16, 14px);
  font-weight: var(--inistate-font-weight-medium, 500);
  color: var(--inistate-text-primary-color);
}

.starter.selected .starter__name {
  color: var(--fts-primary);
}

.starter__desc {
  font-size: 12px;
  line-height: 1.4;
  color: var(--inistate-text-secondary-color, #878787);
}

.starters__error {
  font-size: 14px;
  color: var(--inistate-text-secondary-color);
}

.studio-link {
  margin: 12px 0 0;
  font-size: 12px;
  color: var(--inistate-text-secondary-color, #878787);

  a {
    color: var(--fts-primary);
    font-weight: var(--inistate-font-weight-medium, 500);
    text-decoration: none;
  }
}

.ready__counts {
  display: flex;
  gap: 24px;
  font-size: 13px;
  color: var(--inistate-text-secondary-color, #595959);

  b {
    display: block;
    font-size: 24px;
    font-weight: var(--inistate-font-weight-medium, 500);
    line-height: 1.1;
    color: var(--inistate-text-primary-color);
  }
}

.ready__note {
  margin: 16px 0 0;
  font-size: 13px;
  color: var(--inistate-text-secondary-color, #878787);
}

.starters__links {
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--inistate-text-secondary-color, #878787);
}

.starters__link {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: var(--inistate-font-weight-medium, 500);
  color: var(--fts-primary);
  cursor: pointer;
}

.starters__link:hover {
  text-decoration: underline;
}

.starters__unavailable {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--inistate-text-secondary-color, #878787);
}

.describe {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.describe__need {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--fts-border, #e0e0e0);
  border-radius: var(--inistate-rounded-4, 4px);
  font: inherit;
  font-size: 14px;
  color: var(--inistate-text-primary-color);
  resize: vertical;
}

.describe__need:focus {
  outline: none;
  border-color: var(--fts-primary);
  box-shadow: 0 0 0 1px var(--fts-primary);
}

.describe__count {
  margin-top: -6px;
  text-align: right;
  font-size: 11px;
  color: var(--inistate-text-secondary-color, #878787);
}

.describe .starters__link {
  align-self: flex-start;
  font-size: 13px;
}

.slide-actions__hint {
  align-self: center;
  font-size: 12px;
  color: var(--inistate-text-secondary-color, #878787);
}

.build-progress {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.build-progress__row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--inistate-text-primary-color);
}

.build-progress__row--pending {
  color: var(--inistate-text-secondary-color, #878787);
}

.build-progress__mark {
  flex: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid var(--inistate-color-neutral-300, #bdbdbd);
}

.build-progress__row--done .build-progress__mark {
  border-color: var(--fts-primary);
  background: var(--fts-primary);
}

.build-progress__row--active .build-progress__mark {
  border-color: var(--inistate-color-primary-100, #d3ecf5);
  border-top-color: var(--fts-primary);
  animation: build-progress-spin 0.8s linear infinite;
}

@keyframes build-progress-spin {
  to {
    transform: rotate(360deg);
  }
}

.answer-summary__head {
  margin-bottom: 2px;
  font-size: 10px;
  font-weight: var(--inistate-font-weight-medium, 500);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--inistate-text-secondary-color, #878787);
}

.answer-summary__row--wrap {
  align-items: flex-start;
}

.answer-summary__row--wrap .answer-summary__value {
  white-space: normal;
  text-align: right;
  overflow-wrap: anywhere;
}

/* Running summary on the question slides (the proposal mockup's right panel). */
.answer-summary {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 320px;
  max-width: 85%;
}

.answer-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid var(--fts-border, #e0e0e0);
  border-radius: var(--inistate-rounded-4, 4px);
  font-size: 13px;
  transition: opacity 0.2s;
}

.answer-summary__row--empty {
  opacity: 0.45;
}

.answer-summary__label {
  flex: none;
  font-size: 12px;
  color: var(--inistate-text-secondary-color, #878787);
}

.answer-summary__value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: var(--inistate-font-weight-medium, 500);
  color: var(--inistate-text-primary-color, #212121);
}

.starter-preview {
  width: 340px;
  max-width: 85%;
  padding: 20px 22px;
  background: #fff;
  border: 1px solid var(--fts-border, #e0e0e0);
  border-radius: var(--inistate-rounded-12, 12px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05), 0 3px 6px -4px rgba(0, 0, 0, 0.08);
}

.starter-preview__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.starter-preview__emoji {
  font-size: 24px;
  line-height: 1;
}

.starter-preview__name {
  font-size: 16px;
  font-weight: var(--inistate-font-weight-medium, 500);
  color: var(--inistate-text-primary-color);
}

.starter-preview__sub {
  font-size: 12px;
  color: var(--inistate-text-secondary-color, #878787);
}

.starter-preview__label {
  margin: 12px 0 6px;
  font-size: 10px;
  font-weight: var(--inistate-font-weight-medium, 500);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--inistate-text-secondary-color, #878787);
}

.starter-preview__states,
.starter-preview__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.starter-preview__step {
  display: inline-flex;
  align-items: center;
}

.starter-preview__state {
  padding: 3px 9px;
  border-radius: var(--inistate-rounded-4, 4px);
  font-size: 11px;
  font-weight: var(--inistate-font-weight-medium, 500);
  white-space: nowrap;
}

.starter-preview__state--start {
  background: #eeeeee;
  color: #595959;
}

.starter-preview__state--progress {
  background: #fff3d6;
  color: #8a5a00;
}

.starter-preview__state--done {
  background: #e3f4ea;
  color: #1e6f43;
}

.starter-preview__state--stopped {
  background: #fbe3e3;
  color: #a12e2e;
}

.starter-preview__arrow {
  margin-left: 6px;
  font-size: 12px;
  color: var(--inistate-color-neutral-300, #bdbdbd);
}

.starter-preview__chip {
  font-size: 11px;
  padding: 2px 7px;
  border: 1px solid var(--fts-border, #e0e0e0);
  border-radius: var(--inistate-rounded-4, 4px);
  color: var(--inistate-text-secondary-color, #595959);
}

.starter-preview__chip--step {
  border-color: var(--inistate-color-primary-100, #d3ecf5);
  color: var(--fts-primary);
}

.starter-preview__foot {
  margin-top: 14px;
  font-size: 12px;
  color: var(--inistate-text-secondary-color, #878787);
}

.secondary-btn {
  padding: 8px 16px;
  border: none;
  background: none;
  font-size: var(--inistate-label-text-16, 16px);
  color: var(--inistate-text-secondary-color);
  border-radius: var(--inistate-rounded-4, 4px);
  cursor: pointer;
  transition: background 0.2s;
}

.secondary-btn:hover:not(:disabled) {
  background: var(--inistate-color-neutral-50, #f6f6f6);
}

.secondary-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* The progress dots share their row with the way out of an optional slide. The row keeps the dots'
   height whether a link shows or not, so the dots and the title stay put from slide to slide. */
.slide-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 12px;
}

/* Skip and "I'll do this later": small, grey, and nowhere near the primary button. */
.skip-link {
  flex: none;
  margin-right: -8px;
  padding: 4px 8px;
  border: none;
  background: none;
  font-size: 13px;
  line-height: 18px;
  color: var(--inistate-text-secondary-color, #878787);
  border-radius: var(--inistate-rounded-4, 4px);
  cursor: pointer;
  transition: color 0.2s;
}

.skip-link:hover:not(:disabled) {
  color: var(--inistate-text-primary-color);
  text-decoration: underline;
}

.skip-link:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-box {
  padding: 10px 14px;
  border-radius: var(--inistate-rounded-4, 4px);
  border: 2px solid var(--fts-primary);
  font-size: var(--inistate-label-text-16, 14px);
  outline: none;
  color: var(--fts-text);
  background: #fff;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--inistate-color-primary-700, #1e6f8b);
  }
}

.back-icon {
  color: var(--fts-default);
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.4s ease, opacity 0.4s ease;
}

.slide-right-enter {
  transform: translateX(30px);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(-30px);
  opacity: 0;
}

@media (max-width: 425px) {
  .card-options {
    flex-direction: column;
  }
}
</style>
